import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Switch,
  useMediaQuery
} from '@mui/material';
import { CloseIcon, ChevronLeftIcon, AccessTimeIcon, DateRangeIcon } from 'icons/materialIcons';
import { useTheme } from '@mui/material/styles';
import { useAuth } from 'contexts/AuthContext';
import { sendLeadEmail } from 'apis/emailApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import placeholderImage from 'images/placeholder-grey-image-rotated.jpg';
import { getCardById } from 'apis/cardApi';

const SendLeadEmailModal = ({ open, onClose, contact }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardInfo, setCardInfo] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { authState } = useAuth();

  // Get current date and time for default values
  const today = new Date();
  const formattedDate = today.toISOString().split('T')[0];
  const formattedTime = today.toTimeString().slice(0, 5);

  useEffect(() => {
    if (open) {
      setSubject('');
      setMessage('');
      setScheduleDate(formattedDate);
      setScheduleTime(formattedTime);
      setIsScheduled(false);
      fetchCardInfo();
    }
  }, [open, formattedDate, formattedTime, contact]);

  // Fetch card information if contact has cardId or use default card
  const fetchCardInfo = async () => {
    try {
      let cardId = null;

      // First try to use cardId from contact
      if (contact?.cardId) {
        cardId = contact.cardId;
      }
      // If no cardId in contact, try to use default card
      else if (authState?.user?.contactCardIds?.length > 0) {
        // Get default card or first card in the list
        cardId = authState.user.contactCardIds.find(id =>
          authState.cardState?.[id]?.is_default
        ) || authState.user.contactCardIds[0];
      }

      // If we found a cardId, fetch the card details
      if (cardId && authState.token) {
        let lastError = null;
        const maxRetries = 3;
        const baseDelay = 1000; // 1 second base delay

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const response = await getCardById(cardId);
            if (response && response.card) {
              setCardInfo({
                name: response.card.contact_info?.fullName || 'Unnamed Card',
                email: response.card.contact_info?.email || authState?.user?.email || '',
                avatar: response.card.profilePictureUrl || null
              });
              return; // Success, exit retry loop
            } else {
              break; // No valid response, don't retry
            }
          } catch (error) {
            lastError = error;

            // If this is the last attempt, throw the error
            if (attempt === maxRetries) {
              break;
            }

            // Calculate delay with exponential backoff
            const delay = baseDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }

        // If we get here, all retries failed or no valid response
        // Set fallback info
        setCardInfo({
          name: authState?.user?.fullName || 'Your Card',
          email: authState?.user?.email || '',
          avatar: null
        });
      } else {
        // If no cardId found, use the user name as fallback
        setCardInfo({
          name: authState?.user?.fullName || 'Your Card',
          email: authState?.user?.email || '',
          avatar: null
        });
      }
    } catch (error) {
      // Set fallback info
      setCardInfo({
        name: authState?.user?.fullName || 'Your Card',
        email: authState?.user?.email || '',
        avatar: null
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleScheduleToggle = () => {
    setIsScheduled(!isScheduled);
  };

  const handleSendEmail = async () => {
    if (!message.trim() || !contact?.leadEmail) return;

    setIsSubmitting(true);
    try {
      // Combine date and time if scheduled
      let scheduledDateTime = null;
      if (isScheduled && scheduleDate && scheduleTime) {
        scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      }

      // Format email data according to the expected structure
      const emailData = {
        to: contact?.leadEmail,
        subject: subject.trim() || '(No subject)', // Use "(No subject)" when empty
        text: message.trim(),
        scheduledDate: scheduledDateTime, // Include scheduled date if present
        templateId: 'd-244a3670aac94d86b4c62b8c2a4316a1' // Use the template ID
      };

      const response = await sendLeadEmail(emailData, authState.token);

      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Email sent successfully!',
          severity: 'success'
        });
        onClose();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to send email',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to send email. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Only disable send button if message is empty or submitting
  const isSendDisabled = message.trim().length === 0 || isSubmitting;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="send-lead-email-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: '95%', md: '1000px' },
            height: { xs: '100%', sm: '700px' },
            maxHeight: '90vh',
            bgcolor: '#ffffff',
            borderRadius: { xs: 0, sm: '30px' },
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              zIndex: 10
            }}
            onClick={onClose}
          >
            <ChevronLeftIcon sx={{ color: '#4F4F4F' }} />
            <Typography sx={{ fontSize: '12px', color: '#4F4F4F', ml: 0.5 }}>Back</Typography>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              zIndex: 10
            }}
          >
            <CloseIcon sx={{ color: '#828282' }} />
          </IconButton>

          <Box
            sx={{
              flex: { xs: 1, md: 0.5 },
              py: { xs: 5, md: 6 }, // Increased vertical padding
              px: { xs: 4, md: 5 }, // Increased horizontal padding
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              pt: { xs: 7, md: 7 }, // Increased top padding for the back button
              overflow: 'hidden',
              bgcolor: '#ffffff',
              borderRadius: { xs: 0, sm: '0' }
            }}
          >
            <Box
              sx={{
                height: '100%',
                overflowY: 'scroll',
                p: 2,
                msOverflowStyle: 'none',  /* IE and Edge */
                scrollbarWidth: 'none',   /* Firefox */
                '&::-webkit-scrollbar': {
                  display: 'none'         /* Chrome, Safari, Opera */
                }
              }}
            >
              <Box sx={{ mb: 5 }}>
                <Typography sx={{ mb: 1.5, fontWeight: 600, fontSize: '14px' }}>
                  <strong>Recipients</strong>
                </Typography>
                <Box
                  sx={{
                    p: '12px 16px 12px 16px', // Increased padding
                    bgcolor: '#F7F7F7',
                    borderRadius: '10px',
                    mb: 3,
                    minHeight: '60px',
                    overflow: 'auto'
                  }}
                >
                  <Chip
                    avatar={<Avatar src={contact?.profilePictureUrl || "/assets/img/pendingIconCardView.png"} />}
                    label={contact?.leadEmail}
                    onDelete={() => { }}
                    sx={{
                      m: 0.5,
                      bgcolor: '#ffffff',
                      borderRadius: '100px',
                      px: 0.5,
                      height: '40px',
                      '& .MuiChip-deleteIcon': {
                        color: '#828282'
                      },
                      boxShadow: '0px 2px 2px rgba(27, 34, 50, 0.05)'
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 5 }}>
                <Typography sx={{ mb: 1.5, fontWeight: 600, fontSize: '14px' }}>
                  <strong>Subject</strong>
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={isSubmitting}
                  sx={{
                    mb: 1,
                    bgcolor: '#F7F7F7',
                    borderRadius: '10px',
                    fontWeight: 500,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 1.8, // Increased padding
                      px: 2.5, // Increased padding
                    }
                  }}
                  inputProps={{
                    style: { fontSize: '14px' }
                  }}
                />
              </Box>

              <Box sx={{ mb: 5 }}>
                <Typography sx={{ mb: 1.5, fontWeight: 600, fontSize: '14px' }}>
                  <strong>Message *</strong>
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Message..."
                  multiline
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isSubmitting}
                  sx={{
                    mb: 1,
                    bgcolor: '#F7F7F7',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 2.5, // Increased padding
                      px: 2.5, // Increased padding
                    }
                  }}
                  inputProps={{
                    style: { fontSize: '14px' }
                  }}
                />
              </Box>

              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <Switch
                    checked={isScheduled}
                    onChange={handleScheduleToggle}
                    color="primary"
                    size="small"
                    sx={{ mr: 1.5 }}
                  />
                  <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    <strong>Schedule</strong>
                  </Typography>
                </Box>

                {isScheduled && (
                  <Box sx={{ display: 'flex', mb: 2, transition: 'all 0.3s' }}>
                    <Box sx={{ display: 'flex', position: 'relative', mr: 1 }}>
                      <TextField
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                          width: '130px',
                          bgcolor: '#F7F7F7',
                          borderRadius: '40px',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: '1px solid #E0E0E0',
                              borderRadius: '40px'
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1,
                            pl: 4,
                            fontSize: '12px'
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <DateRangeIcon
                              sx={{
                                position: 'absolute',
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#828282',
                                fontSize: '20px'
                              }}
                            />
                          ),
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', position: 'relative' }}>
                      <TextField
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                          width: '110px',
                          bgcolor: '#F7F7F7',
                          borderRadius: '40px',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: '1px solid #E0E0E0',
                              borderRadius: '40px'
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1,
                            pl: 4,
                            fontSize: '12px'
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <AccessTimeIcon
                              sx={{
                                position: 'absolute',
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#828282',
                                fontSize: '20px'
                              }}
                            />
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSendEmail}
                disabled={isSendDisabled}
                sx={{
                  bgcolor: isSendDisabled ? '#7F7F7F' : '#000000',
                  color: '#ffffff',
                  py: 1,
                  px: 3,
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '100px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#333333'
                  },
                  minWidth: '175px',
                  height: '40px',
                  boxShadow: 'none',
                  '&:disabled': {
                    color: 'white',
                    bgcolor: '#7F7F7F'
                  }
                }}
              >
                {isScheduled ? 'Schedule email' : 'Send email'}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 0.5,
              bgcolor: '#F7F7F7',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '30px 0 0 30px',
              p: 4
            }}
          >
            <Box
              sx={{
                width: '400px',
                height: '600px',
                border: '2px solid #E0E0E0',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                bgcolor: '#ffffff',
                boxShadow: '0px 24px 60px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,

                  overflow: 'hidden'
                }}
              >
                <img
                  src={placeholderImage}
                  alt="Email Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: 'scaleX(-1)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: 0.8,
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  color: '#828282',
                  fontSize: '12px',
                  lineHeight: '18px',
                  zIndex: 10
                }}
              >
                via Touch
              </Box>

              <Divider sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 10 }} />

              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  zIndex: 10,
                  padding: '20px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#757575',
                    mb: 1,
                    fontWeight: 700
                  }}
                >
                  From
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333333',
                      lineHeight: 1.2
                    }}
                  >
                    {cardInfo?.name || 'Your Name'} via Touch
                  </Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: '100px',
                  left: 0,
                  width: '100%',
                  zIndex: 10,
                  padding: '20px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#757575',
                    mb: 1,
                    fontWeight: 700
                  }}
                >
                  Subject
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#333333',
                    mb: 2,
                    fontWeight: 500
                  }}
                >
                  {subject || '(No subject)'}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: '180px',
                  left: 0,
                  width: '100%',
                  height: 'calc(100% - 180px)',
                  zIndex: 10,
                  padding: '20px',
                  overflow: 'auto'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#757575',
                    mb: 1,
                    fontWeight: 700
                  }}
                >
                  Message
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#333333',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontWeight: 500
                  }}
                >
                  {message || 'Your message will appear here...'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default SendLeadEmailModal;